import AbstractPiniaModel from './AbstractPiniaModel'
import { useSubCategoriesStore } from '../pinia/stores/subCategories'
import { useQuestionsStore } from '../pinia/stores/questions'
import { Block, Question } from 'Models'
import Api from 'Ajax'

export class SubCategories extends AbstractPiniaModel {
    //#region Instance Code
    constructor(subCategory) {
        super(subCategory)
    }

    id() {
        return Number(this.source.id)
    }
    name() {
        return this.source.name
    }
    type() {
        return this.source.aufgaben_typ
    }
    displayType() {
        return this.source.aufgaben_anzeige
    }
    introduction() {
        return this.introTextTop()
    }
    explanation() {
        return this.introTextBottom()
    }
    introTextTop() {
        return this.source.text_einleitung_oben
    }
    textConstantTop() {
        return this.source.text_konstant_oben
    }
    introTextBottom() {
        return this.source.text_einleitung_unten
    }
    textConstantBottom() {
        return this.source.text_konstant_unten
    }
    options() {
        return this.source.options
    }
    optionsJson() {
        return JSON.parse(this.source.options)
    }
    textToMemorize() {
        return super.getByAliases(['merktext'], '')
    }
    memorizationInterstitialHtml() {
        return super.getByAliases(['memorization_interstitial_html'], '')
    }
    timeToMemorize() {
        return Number(super.getByAliases(['merkzeit'], 0))
    }
    insertMaxDurationSeconds() {
        return Number(super.getByAliases(['insert_max_duration_seconds'], 0))
    }
    isTextOnly() {
        return this.type() === 'nur_text' ? true : false
    }

    allBlocks() {
        if (!this.source || !this.source.blocks) return []

        return this.source.blocks.map((x) => Block.create(x))
    }

    blocks(altCurrentQuestions = null) {
        if (!this.source || !this.source.blocks) return []

        const currentQuestions = () =>
            altCurrentQuestions !== null
                ? altCurrentQuestions
                : Question.currentQuestions()
        const questionsForBlock = (b) => (q) =>
            q.blockNumber() === b.blockNumber()
        const necessaryBlocks = (b) =>
            currentQuestions().filter(questionsForBlock(b)).length > 0
        const blocks = () => this.source.blocks.map((x) => Block.create(x))

        return blocks().filter(necessaryBlocks)
    }

    currentBlock() {
        if (!this.hasBlocks()) return undefined
        return this.blocks().find(
            (b) => b.blockNumber() === Question.currentQuestion().blockNumber()
        )
    }

    textTopAreaStyles() {
        return this.getByAliases(['text_oben_css'], '')
    }
    textConstTopAreaStyles() {
        return this.getByAliases(['text_konstant_oben_css'], '')
    }

    plainNumberOfQuestions() {
        return Number(this.source.aufgaben_anzahl || 0)
    }

    numberOfCurrentQuestions() {
        return (
            this.numberOfCurrentCorrectQuestions() +
            this.numberOfCurrentWrongQuestions() +
            this.numberOfCurrentUneditedQuestions()
        )
    }

    numberOfCurrentCorrectQuestions() {
        return useQuestionsStore().currentCorrectQuestionsCount
    }
    numberOfCurrentWrongQuestions() {
        return useQuestionsStore().currentWrongQuestionsCount
    }
    numberOfCurrentUneditedQuestions() {
        return useQuestionsStore().currentUneditedQuestionsCount
    }

    absoluteNumberOfQuestions() {
        return (
            this.absoluteNumberOfCorrectQuestions() +
            this.absoluteNumberOfWrongQuestions() +
            this.absoluteNumberOfUneditedQuestions()
        )
    }

    absoluteNumberOfCorrectQuestions() {
        return useQuestionsStore().absoluteCorrectQuestionsCount
    }
    absoluteNumberOfWrongQuestions() {
        return useQuestionsStore().absoluteWrongQuestionsCount
    }
    absoluteNumberOfUneditedQuestions() {
        return useQuestionsStore().absoluteUneditedQuestionsCount
    }

    updateAnsweredQuestionsStates() {
        if (SubCategories.getSelected().id() !== this.id()) return

        const questionSources = this.resolvedExerciseQuestionSources()
        if (questionSources.length <= 0) return

        this.source.aufgaben_anzahl_korrekte =
            this.calcNumOfCorrectQuestionsFor(questionSources)
        this.source.aufgaben_anzahl_falsche =
            this.calcNumOfWrongQuestionsFor(questionSources)
        this.source.aufgaben_anzahl_unbearbeitete =
            this.calcNumOfUneditedQuestionsFor(questionSources)
    }

    resolvedExerciseQuestionSources() {
        const subCategoryId = Number(this.id())
        const pool = useQuestionsStore().allQuestions.find(
            (entry) => Number(entry.subCategoryID) === subCategoryId
        )

        if (pool && Array.isArray(pool.questions) && pool.questions.length > 0)
            return pool.questions

        const unfiltered = useQuestionsStore().unfilteredCurrentQuestions
        if (
            Array.isArray(unfiltered)
            && unfiltered.length > 0
            && Number(unfiltered[0].subCategoryID) === subCategoryId
        ) {
            return unfiltered
        }

        return []
    }

    calcNumOfCurrentUnfilteredQuestions() {
        const questionSources = this.resolvedExerciseQuestionSources()
        if (questionSources.length > 0)
            return this.calcNumOfTotalQuestionsFor(questionSources)

        return this.calcNumOfCurrentFilteredQuestions()
    }

    calcNumOfCorrectUnfilteredQuestions() {
        const questionSources = this.resolvedExerciseQuestionSources()
        if (questionSources.length > 0)
            return this.calcNumOfCorrectQuestionsFor(questionSources)

        return this.calcNumOfCorrectFilteredQuestions()
    }

    calcNumOfWrongUnfilteredQuestions() {
        const questionSources = this.resolvedExerciseQuestionSources()
        if (questionSources.length > 0)
            return this.calcNumOfWrongQuestionsFor(questionSources)

        return this.calcNumOfWrongFilteredQuestions()
    }

    calcNumOfUneditedUnfilteredQuestions() {
        const questionSources = this.resolvedExerciseQuestionSources()
        if (questionSources.length > 0)
            return this.calcNumOfUneditedQuestionsFor(questionSources)

        return this.calcNumOfUneditedFilteredQuestions()
    }

    questionCountFromLoadedQuestions(mainCategoryID, questionPools = null, unfilteredQuestions = null) {
        const pools = questionPools ?? useQuestionsStore().allQuestions
        const entry = pools.find(
            (pool) =>
                Number(pool.mainCategoryID) === Number(mainCategoryID)
                && Number(pool.subCategoryID) === Number(this.id())
        )

        if (entry && Array.isArray(entry.questions) && entry.questions.length > 0) {
            return this.calcNumOfFilteredQuestionsFor(entry.questions)
        }

        const resolvedSources = this.resolvedExerciseQuestionSources()
        if (resolvedSources.length > 0) {
            return this.calcNumOfFilteredQuestionsFor(resolvedSources)
        }

        return null
    }

    calcNumOfTotalQuestionsFor(questionSources) {
        return (
            this.calcNumOfCorrectQuestionsFor(questionSources) +
            this.calcNumOfWrongQuestionsFor(questionSources) +
            this.calcNumOfUneditedQuestionsFor(questionSources)
        )
    }

    calcNumOfFilteredQuestionsFor(questionSources) {
        let currentCount = 0
        if (Question.areCorrectQuestionsVisible()) {
            currentCount += this.calcNumOfCorrectQuestionsFor(questionSources)
        }
        if (Question.areWrongQuestionsVisible()) {
            currentCount += this.calcNumOfWrongQuestionsFor(questionSources)
        }
        if (Question.areUneditedQuestionsVisible()) {
            currentCount += this.calcNumOfUneditedQuestionsFor(questionSources)
        }
        return currentCount
    }

    calcNumOfCurrentFilteredQuestions() {
        return (
            this.calcNumOfCorrectFilteredQuestions() +
            this.calcNumOfWrongFilteredQuestions() +
            this.calcNumOfUneditedFilteredQuestions()
        )
    }

    calcNumOfCorrectFilteredQuestions() {
        return this.source.aufgaben_anzahl_korrekte
    }

    calcNumOfWrongFilteredQuestions() {
        return this.source.aufgaben_anzahl_falsche
    }

    calcNumOfUneditedFilteredQuestions() {
        return this.source.aufgaben_anzahl_unbearbeitete
    }

    calcNumOfCorrectQuestionsFor(questionSources) {
        const makeQuestion = (source) => Question.create(source)
        const addRealCount = (acc, q) => acc + q.realCorrectCount()
        return questionSources.map(makeQuestion).reduce(addRealCount, 0)
    }

    calcNumOfWrongQuestionsFor(questionsSources) {
        const makeQuestion = (source) => Question.create(source)
        const addRealCount = (acc, q) => acc + q.realWrongCount()
        return questionsSources.map(makeQuestion).reduce(addRealCount, 0)
    }

    calcNumOfUneditedQuestionsFor(questionSources) {
        const makeQuestion = (source) => Question.create(source)
        const addRealCount = (acc, q) => acc + q.realUneditedCount()
        return questionSources.map(makeQuestion).reduce(addRealCount, 0)
    }

    durationIsPerQuestion() {
        return this.source.duration_is_per_question === 1
    }

    exampleQuestion() {
        return Question.create(this.source.beispielaufgabe)
    }

    exampleText() {
        const et = this.getByAliases(['example_text'], 'Hierzu ein Beispiel:')
        if (et === '-') return ''
        return et
    }
    hideExampleSolution() {
        return this.getByAliases(['hide_example_solution'], '')
    }

    hasIntro() {
        return !!this.introTextTop()
    }
    hasExampleQuestion() {
        return !this.exampleQuestion().isEmpty()
    }
    hasTextToMemorize() {
        return this.textToMemorize() !== ''
    }
    hasMemorizationPhase() {
        if (this.timeToMemorize() > 0 || this.hasTextToMemorize()) {
            return true
        }

        return this.allBlocks().some(
            (block) => block.hasTextToMemorize() || block.timeToMemorize() > 0
        )
    }
    hasMemorizationInterstitialHtml() {
        return this.memorizationInterstitialHtml().trim() !== ''
    }

    hasBlocks(altCurrentQuestions = null) {
        return this.blocks(altCurrentQuestions).length > 0
    }

    isPersonalityTest() {
        return Boolean(
            Question.currentQuestions().find((q) => q.isPersonalityTest())
        )
    }

    isProfessionTypeTest() {
        return Boolean(
            Question.currentQuestions().find((q) => q.isProfessionTypeTest())
        )
    }
    //#region Instance Code

    //#region Static Methods
    static piniaStore() {
        return useSubCategoriesStore()
    }

    static create(subCategoriesSource) {
        return new SubCategories(subCategoriesSource)
    }
    static createFromId(id) {
        return SubCategories.findById(id)
    }

    static insertedFromSubCategoryIDFor(question) {
        if (!question || (typeof question.isEmpty === 'function' && question.isEmpty()))
            return 0

        if (typeof question.insertedFromSubCategoryID === 'function')
            return Number(question.insertedFromSubCategoryID() || 0)

        return Number(
            question.insertedFromSubCategoryID
                ?? question.insertedFromSubCategoryId
                ?? question.inserted_from_sub_category_id
                ?? 0
        )
    }

    static isInsertedIntermediateQuestion(question) {
        if (!question || (typeof question.isEmpty === 'function' && question.isEmpty()))
            return false

        const insertedFromSubCategoryID = this.insertedFromSubCategoryIDFor(question)
        if (insertedFromSubCategoryID > 0)
            return true

        const selectedSubCategoryID = this.getSelected() && typeof this.getSelected().id === 'function'
            ? Number(this.getSelected().id() || 0)
            : 0
        const questionSubCategoryID = typeof question.subCategoryID === 'function'
            ? Number(question.subCategoryID() || 0)
            : Number(question.subCategoryID || question.sub_category_id || 0)

        return selectedSubCategoryID > 0 && questionSubCategoryID > 0 && questionSubCategoryID !== selectedSubCategoryID
    }

    static blockContextSubCategoryFor(question) {
        const insertedFromSubCategoryID = this.insertedFromSubCategoryIDFor(question)
        if (insertedFromSubCategoryID > 0)
            return this.createFromId(insertedFromSubCategoryID)

        return this.getSelected()
    }

    static questionsInSameBlockContext(question) {
        const insertedFromSubCategoryID = this.insertedFromSubCategoryIDFor(question)

        return Question.currentQuestions().filter((currentQuestion) =>
            this.insertedFromSubCategoryIDFor(currentQuestion) === insertedFromSubCategoryID
        )
    }

    static hasBlocksForQuestion(question) {
        const subCategory = this.blockContextSubCategoryFor(question)
        if (!subCategory || (typeof subCategory.isEmpty === 'function' && subCategory.isEmpty()))
            return false

        return subCategory.hasBlocks(this.questionsInSameBlockContext(question))
    }

    static currentBlockForQuestion(question) {
        const subCategory = this.blockContextSubCategoryFor(question)
        if (!subCategory || !this.hasBlocksForQuestion(question))
            return undefined

        return subCategory.blocks(this.questionsInSameBlockContext(question)).find(
            (block) => block.blockNumber() === question.blockNumber()
        )
    }

    static findById(id) {
        return this.create(this.piniaStore().findSubCategoryById(id))
    }
    static getAll() {
        return this.piniaStore().allSubCategories.map((sc) =>
            SubCategories.create(sc)
        )
    }
    static getSelectedOnes() {
        return this.piniaStore().selectedSubCategories.map((sc) =>
            SubCategories.create(sc)
        )
    }
    static getByProfessionAndMainCategory(professionID, mainCategoryID) {
        return this.piniaStore()
            .allSubCategories.filter(
                (sc) =>
                    sc.professionID === professionID &&
                    sc.mainCategoryID === mainCategoryID
            )
            .map((sc) => SubCategories.create(sc))
    }
    static getSelected() {
        return SubCategories.create(this.piniaStore().selectedSubCategory)
    }
    static allQuestionsAtOnce() {
        return this.piniaStore().allQuestionsAtOnceInSubCategory
    }

    static select(subCategory) {
        this.dispatch('selectSubCategory', subCategory.getSource())
    }
    static selectById(id) {
        this.dispatch('selectSubCategoryById', id)
    }
    static setAll(subCategories) {
        this.dispatch(
            'setAllSubCategories',
            subCategories.map((s) => s.getSource())
        )
    }
    static mergeKnownSubCategories(subCategories) {
        this.dispatch(
            'mergeKnownSubCategories',
            subCategories.map((entry) => (entry && typeof entry.getSource === 'function' ? entry.getSource() : entry))
        )
    }
    static setSelectedOnes(subCategories) {
        this.dispatch(
            'initSubCategories',
            subCategories.map((s) => s.getSource())
        )
    }
    static initSelectedOnes(subCategoriesSources) {
        this.dispatch('initSubCategories', subCategoriesSources)
    }
    static addToLoadedSubCategories(mainCategoryID, subCategories) {
        this.dispatch('addToLoadedSubCategories', {
            mainCategoryID,
            subCategories: subCategories.map((sc) => sc.getSource()),
        })
    }

    static resetAll() {
        this.dispatch('resetAllInSubCategories')
    }

    static loadSubCategoriesFor(professionID, mainCategoryID) {
        if (!professionID || mainCategoryID == null || mainCategoryID === '') {
            return Promise.resolve([])
        }
        if (this.doCategoriesExistFor(mainCategoryID)) {
            return this.loadLocalSubCategoriesFor(mainCategoryID)
        }
        else {
            return this.loadRemoteSubCategoriesFor(
                professionID,
                mainCategoryID
            )
        }
    }

    static doCategoriesExistFor(mainCategoryID) {
        return this.piniaStore().doCategoriesExistFor(mainCategoryID)
    }

    static loadLocalSubCategoriesFor(mainCategoryID) {
        let subCategories =
            this.piniaStore().getAllLoadedSubCategoriesFor(mainCategoryID)
        this.dispatch('initSubCategories', subCategories)
        return Promise.resolve(subCategories)
    }

    // apiInstance is needed in institution context, for preview
    static loadRemoteSubCategoriesFor(professionID, mainCategoryID, apiInstance = Api) {
        if (!professionID || mainCategoryID == null || mainCategoryID === '') {
            return Promise.resolve([])
        }
        this.setSelectedOnes([])

        try {
            const getStackTrace = () => {
                let stack = new Error().stack || ''
                stack = stack.split('\n').map(function (line) {
                    return line.trim()
                })
                return stack.splice(stack[0] == 'Error' ? 2 : 1)
            }
        } catch (e) {
            console.error(e)
        }

        return apiInstance.getSubCategoriesFor(professionID, mainCategoryID).then(
            (response) => {
                const subCategories = response.data || []

                let payload = {
                    mainCategoryID: mainCategoryID,
                    subCategories: subCategories,
                }
                
                this.dispatch('addToLoadedSubCategories', payload)
                this.dispatch('initSubCategories', subCategories)
            }
        )
    }
    //#endregion Static Methods
}
